import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ConstareAIPanel,
  ConstareAIPanelProvider,
} from '../components/ConstareAIPanel';

/**
 * Lesson 5.5 visual preview at /preview/lesson/5.5
 * Week 5 capstone: "The chain is yours now: ship one real thing"
 * 8-section format like 3.5 and 4.5.
 */

export default function LessonPreviewPage5_5() {
  return (
    <ConstareAIPanelProvider>
      <LessonPage />
    </ConstareAIPanelProvider>
  );
}

const SECTION_IDS = [
  'sec-brief',
  'sec-map',
  'sec-stage-1',
  'sec-stage-2',
  'sec-stage-3',
  'sec-stage-4',
  'sec-forward',
  'sec-lock-card',
];

function LessonPage() {
  const totalSections = 8;
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const vis = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (vis[0]) {
          const idx = SECTION_IDS.indexOf(vis[0].target.id);
          if (idx >= 0) setActiveIndex(idx);
        }
      },
      { rootMargin: '-30% 0px -55% 0px', threshold: [0, 0.25, 0.5, 0.75, 1] }
    );
    SECTION_IDS.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const progressPct = useMemo(
    () => Math.round(((activeIndex + 1) / totalSections) * 100),
    [activeIndex]
  );

  return (
    <div className="relative -mx-6 -my-10 min-h-screen bg-ink">
      <div className="sticky top-0 z-20 border-b border-line bg-ink/95 backdrop-blur">
        <div className="mx-auto flex max-w-content items-center gap-6 px-6 py-3">
          <Link to="/dashboard" className="text-xs text-muted hover:text-text">
            ← Home
          </Link>
          <div className="hidden flex-1 sm:block">
            <div className="flex items-center gap-3">
              <div className="h-1 flex-1 overflow-hidden rounded-full bg-elevated">
                <div
                  className="h-full bg-amber transition-all duration-300"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
              <span className="font-mono text-[10px] uppercase tracking-widest text-muted">
                {activeIndex + 1} / {totalSections}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto grid max-w-content gap-8 px-6 py-10 lg:grid-cols-[210px_minmax(0,1fr)_380px]">
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <SectionNav activeIndex={activeIndex} />
          </div>
        </aside>
        <article className="min-w-0">
          <LessonHeader />
          <Brief />
          <Map />
          <Stage1 />
          <Stage2 />
          <Stage3 />
          <Stage4 />
          <CarryForward />
          <LockCard />
          <NextUp />
        </article>
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <ConstareAIPanel />
          </div>
        </aside>
      </div>
    </div>
  );
}

function LessonHeader() {
  return (
    <header className="mb-12">
      <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-amber">
        Week 5 · Taking the chain into the wild · Lesson 5.5 · Capstone
      </p>
      <h1 className="mt-3 text-4xl font-semibold leading-[1.1] tracking-tight text-text">
        The chain is yours now: ship one real thing
      </h1>
      <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-muted">
        <span className="flex items-center gap-1.5">
          <ClockIcon /> 60 min
        </span>
        <span className="text-line">|</span>
        <span>Threads: Voice · Stakes · Grounding · Persona · Receipt</span>
      </div>
      <p className="mt-3 text-xs font-mono uppercase tracking-wider text-amber">
        One real artifact. One real reader. You ship it.
      </p>
    </header>
  );
}

/* --- Section 1: Brief --- */
function Brief() {
  return (
    <Section id="sec-brief" kicker="The brief" title="Pick one real thing from your life. Run the whole chain. Send it.">
      <div className="mt-6 rounded-xl border border-amber/40 bg-amber/[0.04] p-6">
        <p className="font-mono text-[11px] uppercase tracking-wider text-amber">
          Assignment
        </p>
        <h3 className="mt-2 text-xl font-semibold text-text leading-snug">
          Choose one real artifact from outside school. Run Weeks 1 through 5
          on it. Ship it to the real reader before the end of the lesson.
        </h3>
        <div className="mt-5 grid gap-3 sm:grid-cols-2">
          <BriefRow label="What" value="One real thing. Cover letter, ask email, public post, professional email. Your pick." />
          <BriefRow label="Reader" value="A real person with a real role, not your English teacher." />
          <BriefRow label="Length" value="As long as the artifact requires. Most are under 400 words." />
          <BriefRow label="Ship" value="Hit send. Post the post. Mail the letter. Submit the application. By end of lesson." />
        </div>
      </div>

      <p className="mt-6 text-text/90">
        This is the capstone for the whole halfway-point of the program. Every
        move from Weeks 1 through 5 runs on one real artifact. The reader is
        not a teacher. The artifact is not a draft. The ship is the actual
        send.
      </p>

      <Subhead n={1} title="Four artifact options" className="mt-10" />
      <p className="mt-4 text-text/90">
        Pick one. Each one maps to one of the Week 5 lessons. The other three
        moves still apply.
      </p>
      <ArtifactOptions />

      <div className="mt-6 rounded-lg border border-line bg-surface p-5">
        <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
          Before you read on
        </p>
        <p className="mt-3 text-text/90">
          Pick an artifact right now. Write the artifact type and the
          reader&apos;s name at the top of a piece of paper. If you cannot name
          a real reader, the artifact is not real enough. Pick a different one.
        </p>
      </div>
    </Section>
  );
}

function BriefRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-line bg-surface p-3">
      <p className="font-mono text-[10px] uppercase tracking-wider text-muted">
        {label}
      </p>
      <p className="mt-1.5 text-sm text-text/90">{value}</p>
    </div>
  );
}

const ARTIFACTS = [
  {
    n: '01',
    name: 'Cover letter or personal statement',
    lesson: '5.1',
    accent: 'emerald' as const,
    reader: 'A real hiring manager, admissions reader, or program director.',
    use_for: 'A summer job, a program application, a college essay supplement, a scholarship.',
    write_about: 'The one reason this reader should pick you, anchored in one moment from your actual life.',
  },
  {
    n: '02',
    name: 'Ask email',
    lesson: '5.2',
    accent: 'amber' as const,
    reader: 'A teacher, professor, or advisor who already knows who you are.',
    use_for: 'A rec letter, a research spot, a meeting request, a grade appeal, a registration override.',
    write_about: 'Which student this is, work the reader already knows, what you are asking, by when.',
  },
  {
    n: '03',
    name: 'Public post',
    lesson: '5.3',
    accent: 'emerald' as const,
    reader: 'Strangers scrolling. One imagined reader inside the crowd.',
    use_for: 'LinkedIn, Substack, school paper op-ed, an essay on your own site.',
    write_about: 'One specific moment, one contradiction, or one sentence on a topic you have not seen written.',
  },
  {
    n: '04',
    name: 'Professional email',
    lesson: '5.4',
    accent: 'amber' as const,
    reader: 'A teacher, hiring manager, vendor, or service provider whose response actually matters.',
    use_for: 'An outreach, a request, a follow-up, a dispute, an introduction.',
    write_about: 'A real ask, with real facts, real proof, and a real next action.',
  },
];

function ArtifactOptions() {
  return (
    <div className="mt-6 space-y-4">
      {ARTIFACTS.map((a) => {
        const isEm = a.accent === 'emerald';
        const borderCls = isEm ? 'border-emerald-500/40' : 'border-amber/40';
        const bgCls = isEm ? 'bg-emerald-500/[0.04]' : 'bg-amber/[0.04]';
        const labelCls = isEm ? 'text-emerald-300' : 'text-amber';
        return (
          <div key={a.n} className={`rounded-lg border ${borderCls} ${bgCls} p-5`}>
            <div className="flex flex-wrap items-baseline gap-3">
              <span className="font-mono text-xs text-muted">{a.n}</span>
              <h4 className={`text-lg font-semibold ${labelCls}`}>{a.name}</h4>
              <span className="font-mono text-[10px] uppercase tracking-wider text-muted">
                Lesson {a.lesson}
              </span>
            </div>
            <div className="mt-4 grid gap-4 sm:grid-cols-3">
              <ArtifactBlock label="Reader" body={a.reader} />
              <ArtifactBlock label="Use for" body={a.use_for} />
              <ArtifactBlock label="Write about" body={a.write_about} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ArtifactBlock({ label, body }: { label: string; body: string }) {
  return (
    <div>
      <p className="font-mono text-[10px] uppercase tracking-wider text-muted">
        {label}
      </p>
      <p className="mt-1.5 text-sm text-text/85">{body}</p>
    </div>
  );
}

/* --- Section 2: Map --- */
function Map() {
  return (
    <Section id="sec-map" kicker="The map" title="The whole program on one page. Today you run all of it.">
      <p className="mt-6 text-text/90">
        Five weeks of moves. Today the artifact runs through every one of them
        in sequence. Each stage in this lesson collapses a full week into one
        pass on your artifact.
      </p>
      <MapDiagram />
      <div className="mt-6 rounded-lg border border-line bg-surface p-5">
        <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
          The vocab spine you have built
        </p>
        <p className="mt-3 text-text/90 leading-relaxed">
          <span className="font-mono text-[10px] text-amber mr-2">W1</span>
          Token, Embedding, Context window, Parameters, Temperature, Inference, Prompt, RAG, Agents, Training cutoff, Modality ·
          <span className="font-mono text-[10px] text-amber mx-2">W2</span>
          Tool use, Prompt anatomy, Hallucination, Cognitive offloading ·
          <span className="font-mono text-[10px] text-amber mx-2">W3</span>
          Chaining, Primary source, Voice fingerprint, Provenance ·
          <span className="font-mono text-[10px] text-amber mx-2">W4</span>
          Human-in-the-loop, Mode collapse, Particular, Auditability ·
          <span className="font-mono text-[10px] text-amber mx-2">W5</span>
          Stakes, Grounding, Persona, Receipt, Voice
        </p>
      </div>
      <div className="mt-6 rounded-lg border border-amber/40 bg-amber/[0.04] p-5">
        <p className="font-mono text-[11px] uppercase tracking-wider text-amber">
          The order is the move
        </p>
        <p className="mt-3 text-text/90">
          Reader and stakes first. Then the five moves. Then the chain. Then
          ship. Skip the order and you save twenty minutes and lose the work.
          The order is the difference between a draft and an artifact.
        </p>
      </div>
    </Section>
  );
}

const WEEKS = [
  {
    n: '01',
    name: 'Week 1',
    title: 'Constare',
    accent: 'emerald' as const,
    output: 'You verify before you trust. AI helps. AI also confidently invents. You do not ship anything you cannot stand behind.',
  },
  {
    n: '02',
    name: 'Week 2',
    title: 'How AI thinks',
    accent: 'amber' as const,
    output: 'Tokens, hallucination, context, prompts. You drive AI on purpose. You know what to expect and what to catch.',
  },
  {
    n: '03',
    name: 'Week 3',
    title: 'The chain',
    accent: 'emerald' as const,
    output: 'Decide, decompose, source, draft, audit, ship. The four-link sequence that turns AI from a toy into a teammate.',
  },
  {
    n: '04',
    name: 'Week 4',
    title: 'The five moves',
    accent: 'amber' as const,
    output: 'Decide what matters. Notice what is specific. Connect. Defend. Care about the reader. The work AI cannot do.',
  },
  {
    n: '05',
    name: 'Week 5',
    title: 'In the wild',
    accent: 'emerald' as const,
    output: 'Stakes change the math. Grounding names you. Persona picks the reader. Receipt names the next action. Voice is what holds.',
  },
];

function MapDiagram() {
  return (
    <div className="mt-6 grid gap-3 sm:grid-cols-2">
      {WEEKS.map((w) => {
        const isEm = w.accent === 'emerald';
        const tagCls = isEm ? 'bg-emerald-500/15 text-emerald-300' : 'bg-amber/15 text-amber';
        return (
          <div key={w.n} className="rounded-lg border border-line bg-surface p-4">
            <div className="flex flex-wrap items-baseline gap-2">
              <span className="font-mono text-[10px] text-muted">{w.n}</span>
              <span
                className={`rounded px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider ${tagCls}`}
              >
                {w.name}
              </span>
            </div>
            <p className="mt-3 font-mono text-[10px] uppercase tracking-wider text-muted">
              {w.title}
            </p>
            <p className="mt-2 text-sm text-text/85">{w.output}</p>
          </div>
        );
      })}
    </div>
  );
}

/* --- Section 3: Stage 1 Reader + Stakes + Grounding --- */
function Stage1() {
  return (
    <Section id="sec-stage-1" kicker="Stage 1 · Set up the reader" title="Who. Why. What changes if they say yes.">
      <p className="mt-6 text-text/90">
        Before you write a sentence, do the reader work from 4.5 plus the
        stakes work from 5.1 and the grounding work from 5.2. AI does none of
        this. You will spend ten minutes here. The minutes are the work.
      </p>

      <StageBlock
        label="The move you make on paper"
        accent="emerald"
        body="Write the reader&rsquo;s name and role at the top. Three lists: what they know, what they do not know, what they care about right now. Below the lists, one line: what changes if they say yes to this artifact. Below that, one line: which student, applicant, or employee you are and what work of yours the reader already knows. Those last two lines are stakes and grounding in one paragraph."
      />

      <StageBlock
        label="The prompt you give AI"
        accent="amber"
        body="&ldquo;Here is the reader: [paragraph]. Here is the artifact: [type]. Here is what I want to happen: [outcome]. List five things this reader probably wants to hear first. List five things this reader probably never wants to hear from a stranger. Do not draft anything.&rdquo; AI helps you see the reader from outside your head. You decide which items are true."
      />

      <StageBlock
        label="The audit move you do right here"
        accent="emerald"
        body="Cross out anything on the AI lists that does not match your actual reader. AI is guessing. Underline anything that surprised you in a good way. The underlined items are the structure of your opening paragraph."
      />

      <div className="mt-6 rounded-lg border border-line bg-surface p-5">
        <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
          Output of this stage
        </p>
        <ul className="mt-3 space-y-2 text-sm text-text/90">
          <li>
            <span className="font-mono text-[10px] text-amber mr-2">→</span>
            One reader, named, with role.
          </li>
          <li>
            <span className="font-mono text-[10px] text-amber mr-2">→</span>
            One line on stakes: what changes if they say yes.
          </li>
          <li>
            <span className="font-mono text-[10px] text-amber mr-2">→</span>
            One line on grounding: which student, applicant, or employee you are and the work the reader already knows.
          </li>
        </ul>
      </div>
    </Section>
  );
}

/* --- Section 4: Stage 2 Through-line + Opening --- */
function Stage2() {
  return (
    <Section id="sec-stage-2" kicker="Stage 2 · Through-line and opening" title="One sentence the artifact is about. One opening the reader cannot scroll past.">
      <p className="mt-6 text-text/90">
        Now the writing starts. Through-line first, from 4.2. Then the opening
        line, picked from the four hook types in 5.3. The two have to fit
        together: the hook earns the next sentence, and the next sentence is
        the through-line.
      </p>

      <StageBlock
        label="The through-line move"
        accent="amber"
        body="Write the one-sentence through-line of your artifact. Read it out loud as if you were saying it to the reader on a phone call. If it sounds like an essay sentence, rewrite it as a sentence you would actually say. Through-line plus stakes plus grounding should now all fit on a sticky note."
      />

      <StageBlock
        label="The opening line move"
        accent="emerald"
        body="Pick one of the four hooks from 5.3: a detail, a contradiction, a number, or a line the reader has not seen on this topic. Write the opening line. Then write the next sentence. The next sentence should be a version of your through-line. If the second sentence does not feel earned by the first, the hook is wrong."
      />

      <StageBlock
        label="The prompt that helps"
        accent="amber"
        body="&ldquo;Here is my through-line. Here is my reader. Here is my opening line. Tell me three questions the reader would ask after the opening line. Do not answer them. Do not rewrite anything.&rdquo; The questions show you what the next three paragraphs need to do. The artifact is structured around answering those questions in your voice."
      />

      <div className="mt-6 rounded-lg border border-line bg-surface p-5">
        <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
          Output of this stage
        </p>
        <p className="mt-3 text-text/90">
          A one-sentence through-line in your voice. An opening line using one
          of the four hooks. Three reader questions noted in the margin to
          answer as the artifact unfolds.
        </p>
      </div>
    </Section>
  );
}

/* --- Section 5: Stage 3 Particulars + Receipt --- */
function Stage3() {
  return (
    <Section id="sec-stage-3" kicker="Stage 3 · Particulars and proof" title="One specific second per paragraph. The proof attached if it is an email.">
      <p className="mt-6 text-text/90">
        From 4.3: one particular per paragraph, no exceptions. From 5.4: every
        claim ships with its proof. From 3.3: drafting modes still apply. AI
        builds the structure around your particulars. AI does not write the
        particulars.
      </p>

      <StageBlock
        label="The mining move"
        accent="emerald"
        body="For each section of your artifact, answer three questions on paper: what specific moment from my life relates here, what is one sentence somebody actually said about this, what is one number from my own life that connects. Most of the answers will not make the artifact. One per paragraph will."
      />

      <StageBlock
        label="The drafting move"
        accent="amber"
        body="Write the artifact using one particular per paragraph as the anchor. AI helps with structure: opening, transitions, closing. AI does not write the moments. From 5.4 (if your artifact is an email): every claim has the proof attached in the same message. Names, dates, screenshots, links. If you mention a document, it ships with the email."
      />

      <StageBlock
        label="The trap to watch for"
        accent="emerald"
        body="AI will offer to &ldquo;add a personal touch&rdquo; or invent details that sound right. Catch them. The ask email that names the wrong paper. The cover letter that invents a project you did not work on. The public post that exaggerates a number. Particulars are yours. AI averages feel real and read fake to anyone who can verify."
      />

      <div className="mt-6 rounded-lg border border-line bg-surface p-5">
        <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
          Output of this stage
        </p>
        <p className="mt-3 text-text/90">
          A complete rough draft of the artifact. Every paragraph anchored on
          one particular only you have. If the artifact is an email or a
          letter, the proof is attached or quoted in the body. The through-line
          carries through the close.
        </p>
      </div>
    </Section>
  );
}

/* --- Section 6: Stage 4 Defense + Voice + Ship --- */
function Stage4() {
  return (
    <Section id="sec-stage-4" kicker="Stage 4 · Defense, voice, ship" title="Two passes. Then send it. Actually send it.">
      <p className="mt-6 text-text/90">
        From 4.4: defensible. From 5.4: quote-back safe. From this lesson:
        voice. Voice is the part the reader will recognize if they read
        anything else you write. Voice is what survives across every artifact
        you ship for the next forty years.
      </p>

      <StageBlock
        label="Pass 1 · The defense kit + quote-back test"
        accent="emerald"
        body="Run the 4.4 three-column kit on the draft: claim list, paragraph reasons, AI moves. Then run the 5.4 quote-back test: could any sentence be pasted into a thread tomorrow and still hold up? Cut or rewrite anything that fails either test. Twenty minutes, on paper, before pass two."
      />

      <StageBlock
        label="Pass 2 · The voice pass"
        accent="amber"
        body="Read the whole artifact out loud as if your reader were on the phone with you. Stop on any sentence that does not sound like something you would say. Rewrite it in your words. Pay attention to: corporate phrases AI added, hedges AI inserted, smoothing that erased the specific. The version that sounds like you, after both passes, is voice. The reader will recognize it next time."
      />

      <StageBlock
        label="Ship"
        accent="emerald"
        body="Send the artifact. Hit send on the email. Post the post. Mail the letter. Submit the application. Do not save it for tomorrow. The whole point of the program is that ship is when the reader receives it, not when you feel finished. If something scares you about sending it, find that sentence and rewrite that one. Then send."
      />

      <div className="mt-6 rounded-lg border border-line bg-surface p-5">
        <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
          Output of this stage
        </p>
        <p className="mt-3 text-text/90">
          One sent artifact. One real reader who actually receives it. One
          sentence in your notes about what was hardest in the chain this
          time. The chain ends with the reader on the other side, not with
          submit.
        </p>
      </div>
    </Section>
  );
}

/* --- Section 7: Carry forward --- */
function CarryForward() {
  return (
    <Section id="sec-forward" kicker="What you carry forward" title="Five habits to keep. Five traps to remember.">
      <div className="mt-6 grid gap-6 sm:grid-cols-2">
        <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/[0.04] p-5">
          <p className="font-mono text-[11px] uppercase tracking-wider text-emerald-300">
            Habits to keep
          </p>
          <ul className="mt-4 space-y-3 text-sm text-text/90">
            <li>
              <span className="font-mono text-[10px] text-emerald-300 mr-2">01</span>
              Name the reader, the stakes, and your grounding before you write a
              sentence.
            </li>
            <li>
              <span className="font-mono text-[10px] text-emerald-300 mr-2">02</span>
              Say the through-line out loud. If it sounds like an essay, rewrite
              it as a thing you would say.
            </li>
            <li>
              <span className="font-mono text-[10px] text-emerald-300 mr-2">03</span>
              One particular per paragraph. No exceptions. Yours, not AI&apos;s.
            </li>
            <li>
              <span className="font-mono text-[10px] text-emerald-300 mr-2">04</span>
              Defense kit plus quote-back test before send. Every time.
            </li>
            <li>
              <span className="font-mono text-[10px] text-emerald-300 mr-2">05</span>
              Ship is when the reader receives it. Not when you stop editing.
            </li>
          </ul>
        </div>
        <div className="rounded-lg border border-rose-500/40 bg-rose-500/[0.04] p-5">
          <p className="font-mono text-[11px] uppercase tracking-wider text-rose-300">
            Traps to remember
          </p>
          <ul className="mt-4 space-y-3 text-sm text-text/90">
            <li>
              <span className="font-mono text-[10px] text-rose-300 mr-2">01</span>
              Writing for &ldquo;the average reader.&rdquo; The average reader is no reader.
            </li>
            <li>
              <span className="font-mono text-[10px] text-rose-300 mr-2">02</span>
              Letting AI write the opening line. It will choose the cliché every time.
            </li>
            <li>
              <span className="font-mono text-[10px] text-rose-300 mr-2">03</span>
              Accepting AI-generated particulars. They feel real and verify fake.
            </li>
            <li>
              <span className="font-mono text-[10px] text-rose-300 mr-2">04</span>
              Inflating one moment into a generational claim. Honest scale wins.
            </li>
            <li>
              <span className="font-mono text-[10px] text-rose-300 mr-2">05</span>
              Confusing submit with ship. Submit is for school. Ship is for the real reader.
            </li>
          </ul>
        </div>
      </div>

      <div className="mt-6 rounded-lg border border-line bg-surface p-5">
        <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
          The bigger move
        </p>
        <p className="mt-3 text-text/90">
          Five weeks ago, AI was either a thing to avoid or a thing to lean on.
          Now it is a teammate that does work with you and for you, while you
          do the work it cannot do: judgment, grounding, voice, the actual
          sending. The artifact you shipped today is not the last one. It is
          the first one you made on purpose, with the whole chain.
        </p>
      </div>
    </Section>
  );
}

/* --- Section 8: Lock card --- */
function LockCard() {
  return (
    <Section id="sec-lock-card" kicker="Lock card · Week 5" title="Screenshot this. The first half of the program on one card.">
      <div className="mt-6 rounded-2xl border border-amber/40 bg-gradient-to-b from-amber/[0.06] to-transparent p-7" data-lockcard-body>
        <div className="flex items-start justify-between">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-wider text-amber">
              Week 5 · Lock card · Taking the chain into the wild
            </p>
          </div>
          <button className="font-mono text-[10px] uppercase tracking-wider text-muted hover:text-text">
            ↓ Save image
          </button>
        </div>
        <h3 className="mt-4 text-2xl font-semibold leading-snug text-text">
          AI has impacted everything. The work that decides whether the
          artifact lands is still yours: who, why, what, how, send.
        </h3>

        <div className="mt-6 grid gap-3 sm:grid-cols-2">
          <WeekTile n="01" name="Stakes" lesson="5.1" body="What the reader can give or take. The cover letter and the personal statement." />
          <WeekTile n="02" name="Grounding" lesson="5.2" body="Which student this is. The ask email to a teacher, professor, or advisor." />
          <WeekTile n="03" name="Persona" lesson="5.3" body="One real person scrolling. The public post strangers read." />
          <WeekTile n="04" name="Receipt" lesson="5.4" body="Facts, proof, next action. The email that becomes a record." />
          <WeekTile n="05" name="Voice" lesson="5.5" body="What survives across every artifact you ship. Recognized next time." />
        </div>

        <div className="mt-6 rounded-md border border-line bg-surface p-4">
          <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
            The five-week vocab spine, end to end
          </p>
          <p className="mt-3 text-text/90 leading-relaxed">
            <span className="font-mono text-[10px] text-amber mr-2">W1</span>
            Token, Embedding, Context window, Parameters, Temperature, Inference, Prompt, RAG, Agents, Training cutoff, Modality ·
            <span className="font-mono text-[10px] text-amber mx-2">W2</span>
            Tool use, Prompt anatomy, Hallucination, Cognitive offloading ·
            <span className="font-mono text-[10px] text-amber mx-2">W3</span>
            Chaining, Primary source, Voice fingerprint, Provenance ·
            <span className="font-mono text-[10px] text-amber mx-2">W4</span>
            Human-in-the-loop, Mode collapse, Particular, Auditability ·
            <span className="font-mono text-[10px] text-amber mx-2">W5</span>
            Stakes, Grounding, Persona, Receipt, Voice
          </p>
        </div>

        <p className="mt-6 text-sm text-muted italic">
          End of week 5. Halfway home and the chain is yours. The next three
          weeks are about doing this at higher levels: longer work, harder
          readers, work that lives.
        </p>
      </div>
    </Section>
  );
}

function WeekTile({
  n,
  name,
  lesson,
  body,
}: {
  n: string;
  name: string;
  lesson: string;
  body: string;
}) {
  return (
    <div className="rounded-lg border border-line bg-surface p-4">
      <div className="flex items-baseline justify-between">
        <span className="font-mono text-[10px] text-muted">{n}</span>
        <span className="font-mono text-[10px] text-muted">{lesson}</span>
      </div>
      <div className="mt-2">
        <span className="inline-block rounded bg-amber/15 px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-amber">
          {name}
        </span>
      </div>
      <p className="mt-3 text-sm text-text/85">{body}</p>
    </div>
  );
}

/* --- Next up --- */
function NextUp() {
  return (
    <div className="mt-14 rounded-xl border border-line bg-surface p-6" data-nextup-card>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-xl">
          <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
            Up next · Week 6
          </p>
          <h3 className="mt-2 text-xl font-semibold text-text">
            Longer work that has to hold together
          </h3>
          <p className="mt-3 text-sm text-text/85">
            Five lessons on writing that runs more than a page: research
            papers, project proposals, longform posts, multi-section emails.
            Same chain, longer arc. Harder to keep the through-line. The next
            half of the program is about scaling the moves you already have.
          </p>
        </div>
        <button className="rounded-md border border-amber/40 px-4 py-2 text-sm font-medium text-amber hover:bg-amber/[0.08]">
          Coming soon
        </button>
      </div>
    </div>
  );
}

/* --- Stage helper --- */
function StageBlock({
  label,
  body,
  accent,
}: {
  label: string;
  body: string;
  accent: 'emerald' | 'amber';
}) {
  const isEm = accent === 'emerald';
  const borderCls = isEm ? 'border-emerald-500/40' : 'border-amber/40';
  const bgCls = isEm ? 'bg-emerald-500/[0.04]' : 'bg-amber/[0.04]';
  const labelCls = isEm ? 'text-emerald-300' : 'text-amber';
  return (
    <div className={`mt-5 rounded-lg border ${borderCls} ${bgCls} p-5`}>
      <p className={`font-mono text-[11px] uppercase tracking-wider ${labelCls}`}>
        {label}
      </p>
      <p
        className="mt-3 text-text/90"
        dangerouslySetInnerHTML={{ __html: body }}
      />
    </div>
  );
}

/* --- Primitives --- */

function Section({
  id,
  kicker,
  title,
  children,
}: {
  id: string;
  kicker: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="mt-16 scroll-mt-24">
      <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-amber">
        {kicker}
      </p>
      <h2 className="mt-3 text-2xl font-semibold leading-snug text-text">{title}</h2>
      <div>{children}</div>
    </section>
  );
}

function Subhead({ n, title, className = '' }: { n: number; title: string; className?: string }) {
  return (
    <h3 className={`text-xl font-semibold text-text ${className}`}>
      <span className="mr-3 font-mono text-sm text-amber">{n}</span>
      {title}
    </h3>
  );
}

function SectionNav({ activeIndex }: { activeIndex: number }) {
  const items = [
    'The brief',
    'The map',
    '1 · Reader + stakes',
    '2 · Through-line + open',
    '3 · Particulars + proof',
    '4 · Defense + ship',
    'Carry forward',
    'Lock card',
  ];
  return (
    <nav>
      <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
        In this lesson
      </p>
      <ol className="mt-4 space-y-1">
        {items.map((label, i) => {
          const isActive = i === activeIndex;
          const isPast = i < activeIndex;
          return (
            <li key={label}>
              <a
                href={`#${SECTION_IDS[i]}`}
                className={`flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors ${
                  isActive
                    ? 'bg-elevated text-text'
                    : 'text-muted hover:text-text'
                }`}
              >
                <span
                  className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-mono ${
                    isActive
                      ? 'bg-amber text-ink'
                      : isPast
                      ? 'border border-amber/60 text-amber'
                      : 'border border-line text-muted'
                  }`}
                >
                  {isPast ? '✓' : i + 1}
                </span>
                <span>{label}</span>
              </a>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

function ClockIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-muted">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}
